import React, { useEffect, useState } from "react"
import {
  Container, Card, CardBody, Row, Col, Table, Button, Input, Label,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useSelector } from "react-redux"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import {
  getCamps, createCamp, updateCamp,
  getCampRooms, createRoom, updateRoom, releaseRoom, getRoomPriceHistory, getMoveTargets, getRoomOccupants,
  updateBed, findCampOccupant, allotBed, allotShared, vacateEmployee, searchAllotEmployees,
} from "../../services/campManagement"
import { toastError, toastSuccess } from "../../helpers/msg_helper"

const RANGES = ["Today", "Week", "Month", "Year", "Custom"]
const CATEGORIES = ["WORKER", "OFFICE STAFF", "CAMP STAFF"]
const BED_STATUS = ["occupied", "vacant", "vacation"]

const CampManager = () => {
  const navigate = useNavigate()
  const [urlParams] = useSearchParams()
  document.title = "Camp Manager"
  // Feature flag (Developer → Application Settings): floor & bed-level model on/off.
  const bedLevel = !!useSelector(state => state.Login?.loggedUser?.settings?.camp_floor_bed)

  const [camps, setCamps] = useState([])
  const [selected, setSelected] = useState(null)
  const [rooms, setRooms] = useState([])
  const [range, setRange] = useState("Today")
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")

  const [campModal, setCampModal] = useState(false)
  const [campForm, setCampForm] = useState({ id: null, name: "", location: "", remark: "", rooms: [] })
  const today = new Date().toISOString().slice(0, 10)
  const [roomModal, setRoomModal] = useState(false)
  const [roomForm, setRoomForm] = useState({ id: null, room_no: "", capacity: 0, max_occupancy: "", occupied: 0, category: "WORKER", price: "", hired_date: "" })
  const [releaseModal, setReleaseModal] = useState(false)
  const [releaseForm, setReleaseForm] = useState({ id: null, room_no: "", release_date: "", remark: "" })
  const [bedsModal, setBedsModal] = useState(false)
  const [bedsRoom, setBedsRoom] = useState(null)
  const [bedList, setBedList] = useState([])
  const [occQuery, setOccQuery] = useState("")
  const [occResults, setOccResults] = useState([])
  // room listing search + room allotment
  const [roomQuery, setRoomQuery] = useState("")
  const [allotModal, setAllotModal] = useState(false)
  const [allotEmp, setAllotEmp] = useState(null)
  const [allotQuery, setAllotQuery] = useState("")
  const [allotResults, setAllotResults] = useState([])
  const [allotBedSel, setAllotBedSel] = useState({}) // per-room chosen bed id
  const [allotDate, setAllotDate] = useState(today)  // allotment date for a new stay
  // vacate: capture date + reason + details
  const [vacateModal, setVacateModal] = useState(false)
  const [vacateForm, setVacateForm] = useState({ empId: null, date: today, reason: "", remark: "" })
  // per-room price history viewer
  const [priceHistModal, setPriceHistModal] = useState(false)
  const [priceHistRoom, setPriceHistRoom] = useState(null)
  const [priceHist, setPriceHist] = useState([])
  // per-room occupants manager (move / vacate, then release)
  const [occModal, setOccModal] = useState(false)
  const [occRoom, setOccRoom] = useState(null)      // full room (beds + shared) being managed
  const [occDate, setOccDate] = useState(today)
  const [occReason, setOccReason] = useState("")
  const [occDestSel, setOccDestSel] = useState({})  // per-employee chosen destination room id
  const [occTargets, setOccTargets] = useState([])  // rooms with free space across ALL camps
  // read-only "who's staying here" viewer
  const [occViewModal, setOccViewModal] = useState(false)
  const [occViewRoom, setOccViewRoom] = useState(null)
  const [occViewList, setOccViewList] = useState([])

  // Find a housed person by name / code (across all companies — camps are global)
  useEffect(() => {
    if (occQuery.trim().length < 2) { setOccResults([]); return }
    const t = setTimeout(async () => {
      try { const r = await findCampOccupant(occQuery.trim()); setOccResults(r?.data || []) }
      catch (e) { setOccResults([]) }
    }, 300)
    return () => clearTimeout(t)
  }, [occQuery])

  // Search any workforce employee to allot to a bed (all companies)
  useEffect(() => {
    if (allotEmp || allotQuery.trim().length < 2) { setAllotResults([]); return }
    const t = setTimeout(async () => {
      try { const r = await searchAllotEmployees(allotQuery.trim()); setAllotResults(r?.data || []) }
      catch (e) { setAllotResults([]) }
    }, 300)
    return () => clearTimeout(t)
  }, [allotQuery, allotEmp])

  const loadCamps = async (keepSel = true) => {
    try {
      const r = await getCamps()
      const list = r?.data || []
      setCamps(list)
      // Show just the camp listing by default; only deep-link (?camp=) pre-selects a camp.
      const wantId = urlParams.get("camp")
      const want = wantId && list.find(c => String(c.id) === String(wantId))
      if (want) selectCamp(want)
    } catch (e) { toastError(e?.message || "Failed to load camps") }
  }
  const loadRooms = async campId => {
    try { const r = await getCampRooms(campId); setRooms(r?.data || []) }
    catch (e) { toastError(e?.message) }
  }
  const selectCamp = c => {
    if (selected === c.id) { setSelected(null); setRooms([]) } // click again to collapse back to the list
    else { setSelected(c.id); loadRooms(c.id) }
  }

  useEffect(() => { loadCamps(false) }, [])

  // ---- camp ----
  const newRoomRow = () => ({ room_no: "", capacity: "", max_occupancy: "", hired_date: "", category: "WORKER", price: "" })
  const openNewCamp = () => { setCampForm({ id: null, name: "", location: "", remark: "", rooms: [newRoomRow()] }); setCampModal(true) }
  const openEditCamp = async c => {
    setCampForm({ id: c.id, name: c.name, location: c.location || "", remark: c.remark || "", rooms: [] })
    setCampModal(true)
    try {
      const r = await getCampRooms(c.id)
      const rooms = (r?.data || []).map(rm => ({
        id: rm.id, room_no: rm.room_no, capacity: rm.capacity ?? 0,
        max_occupancy: rm.max_occupancy ?? "",
        hired_date: rm.hired_date ? String(rm.hired_date).slice(0, 10) : "",
        category: rm.category || "WORKER",
        price: rm.price ?? "",
        occupied: rm.occupied ?? 0,
        ceiling: rm.ceiling ?? rm.capacity ?? 0,
        shared: (rm.shared_occupants || []).length,
      }))
      setCampForm(f => (f.id === c.id ? { ...f, rooms } : f))
    } catch (e) { /* leave the rooms table empty if the fetch fails */ }
  }
  // prepend so the new row appears at the top (right under the + Room button), visible without scrolling
  const addCampRoom = () => setCampForm(f => ({ ...f, rooms: [newRoomRow(), ...(f.rooms || [])] }))
  const removeCampRoom = i => setCampForm(f => ({ ...f, rooms: f.rooms.filter((_, idx) => idx !== i) }))
  const setCampRoom = (i, k, v) => setCampForm(f => ({ ...f, rooms: f.rooms.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }))
  const saveCamp = async () => {
    if (!campForm.name.trim()) return toastError("Camp name is required")
    try {
      const rooms = (campForm.rooms || []).filter(r => String(r.room_no).trim())
      if (campForm.id) {
        await updateCamp(campForm.id, { ...campForm, rooms })
        if (selected === campForm.id) loadRooms(selected)
      } else {
        await createCamp({ ...campForm, rooms })
      }
      toastSuccess("Camp saved"); setCampModal(false); loadCamps(false)
    } catch (e) { toastError(e?.message || "Save failed") }
  }

  // ---- room ----
  const openEditRoom = rm => {
    setRoomForm({ id: rm.id, room_no: rm.room_no, capacity: rm.capacity, max_occupancy: rm.max_occupancy ?? "", occupied: rm.occupied, category: rm.category || "WORKER", price: rm.price ?? "", hired_date: rm.hired_date ? String(rm.hired_date).slice(0, 10) : "" })
    setRoomModal(true)
  }

  // ---- room price history ----
  const openPriceHistory = async rm => {
    setPriceHistRoom(rm); setPriceHist([]); setPriceHistModal(true)
    try { const r = await getRoomPriceHistory(rm.id); setPriceHist(r?.data || []) }
    catch (e) { setPriceHist([]) }
  }

  // ---- room occupants: move/vacate everyone, then release ----
  const campIdForRooms = () => campForm.id || selected
  // current occupants of a room = bed occupants + shared occupants
  const roomOccupants = room => {
    if (!room) return []
    const bed = (room.beds || [])
      .filter(b => b.occupant_employee_id && b.occupant)
      .map(b => ({ ...b.occupant, bed_no: b.bed_no, shared: false }))
    const shared = (room.shared_occupants || [])
      .map(s => ({ id: s.employee_id, name: s.name, amj_code: s.amj_code, company: s.company, bed_no: null, shared: true }))
    return [...bed, ...shared]
  }
  const loadOccRoom = async roomId => {
    const [r, t] = await Promise.all([getCampRooms(campIdForRooms()), getMoveTargets(roomId)])
    setOccTargets(t?.data || [])           // rooms with space across ALL camps
    setOccRoom((r?.data || []).find(x => x.id === roomId) || null)
  }
  const openOccupants = async room => {
    setOccRoom(room); setOccTargets([]); setOccDate(today); setOccReason(""); setOccDestSel({})
    setOccModal(true)
    try { await loadOccRoom(room.id) } catch (e) { toastError("Could not load occupants") }
  }
  // read-only occupants details (name / company / how long staying)
  const openViewOccupants = async room => {
    setOccViewRoom(room); setOccViewList([]); setOccViewModal(true)
    try { const r = await getRoomOccupants(room.id); setOccViewList(r?.data || []) }
    catch (e) { setOccViewList([]) }
  }
  const stayDuration = days => {
    if (days == null) return "—"
    if (days < 30) return `${days} day${days === 1 ? "" : "s"}`
    const mo = Math.floor(days / 30), d = days % 30
    return `${mo} mo${d ? ` ${d} d` : ""}`
  }
  const moveOccupant = async emp => {
    const destId = Number(occDestSel[emp.id] || 0)
    if (!destId) return toastError("Pick a room to move them to")
    if (!occDate) return toastError("Pick a date")
    const dest = (occTargets || []).find(t => t.room_id === destId)
    if (!dest) return toastError("That room is no longer available")
    try {
      if (dest.free_bed_id) await allotBed(dest.free_bed_id, { employee_id: emp.id, allot_date: occDate })
      else await allotShared(destId, { employee_id: emp.id, allot_date: occDate })
      toastSuccess(`${emp.name} moved to ${dest.camp_name} · ${dest.room_no}`)
      await loadOccRoom(occRoom.id); await loadRooms(campIdForRooms()); loadCamps()
    } catch (e) { toastError(e?.message || "Move failed") }
  }
  const vacateOccupant = async emp => {
    if (!occReason.trim()) return toastError("Enter a reason (applies to vacated occupants)")
    if (!occDate) return toastError("Pick a date")
    try {
      await vacateEmployee(emp.id, { vacate_date: occDate, vacate_reason: occReason })
      toastSuccess(`${emp.name} vacated`)
      await loadOccRoom(occRoom.id); await loadRooms(campIdForRooms()); loadCamps()
    } catch (e) { toastError(e?.message || "Vacate failed") }
  }
  const releaseFromOccupants = async () => {
    if (!occRoom) return
    try {
      await releaseRoom(occRoom.id, { release_date: occDate, remark: occReason || "room released" })
      toastSuccess("Room released"); setOccModal(false)
      await loadRooms(campIdForRooms()); loadCamps()
      if (campModal && campForm.id) openEditCamp({ id: campForm.id, name: campForm.name, location: campForm.location, remark: campForm.remark })
    } catch (e) { toastError(e?.message || "Release failed") }
  }

  // ---- beds (bed-level occupancy) ----
  const openBeds = rm => { setBedsRoom(rm); setBedList(rm.beds || []); setBedsModal(true) }
  const changeBedStatus = async (bed, status) => {
    setBedList(list => list.map(b => (b.id === bed.id ? { ...b, status } : b)))
    try {
      await updateBed(bed.id, { status })
      loadRooms(selected); loadCamps()
    } catch (e) { toastError(e?.message || "Update failed") }
  }

  // ---- room allotment (find employee → pick a vacant bed) ----
  const openAllot = () => {
    if (!selected) return toastError("Select a camp first")
    setAllotEmp(null); setAllotQuery(""); setAllotResults([]); setAllotBedSel({}); setAllotDate(today)
    setAllotModal(true)
  }
  // beds that can take a new occupant (truly vacant, no occupant)
  const assignableBeds = r => (r.beds || []).filter(b => b.status === "vacant" && !b.occupant_employee_id)
  const allotToBed = async bedId => {
    if (!allotEmp || !bedId) return
    if (!allotDate) return toastError("Pick an allotment date")
    try {
      await allotBed(bedId, { employee_id: allotEmp.id, allot_date: allotDate })
      toastSuccess(`${allotEmp.name} allotted`)
      setAllotEmp(null); setAllotQuery(""); setAllotResults([]); setAllotBedSel({})
      await loadRooms(selected); loadCamps()
    } catch (e) { toastError(e?.message || "Allotment failed") }
  }
  // allot beyond the beds (over-capacity sharing) — used when a room is bed-full but under its ceiling
  const allotSharedToRoom = async (roomId, bedId = null) => {
    if (!allotEmp || !roomId) return
    if (!allotDate) return toastError("Pick an allotment date")
    try {
      await allotShared(roomId, { employee_id: allotEmp.id, allot_date: allotDate, camp_bed_id: bedId })
      toastSuccess(`${allotEmp.name} allotted (shared)`)
      setAllotEmp(null); setAllotQuery(""); setAllotResults([]); setAllotBedSel({})
      await loadRooms(selected); loadCamps()
    } catch (e) { toastError(e?.message || "Allotment failed") }
  }
  // open the vacate dialog for the currently-housed employee (bed OR shared slot)
  const openVacate = () => {
    if (!allotEmp?.id || !allotEmp?.current) return
    setVacateForm({ empId: allotEmp.id, date: today, reason: "", remark: "" })
    setVacateModal(true)
  }
  const doVacate = async () => {
    if (!vacateForm.empId) return
    if (!vacateForm.date) return toastError("Pick a vacate date")
    if (!vacateForm.reason.trim()) return toastError("Reason is required")
    try {
      await vacateEmployee(vacateForm.empId, {
        vacate_date: vacateForm.date,
        vacate_reason: vacateForm.reason,
        vacate_remark: vacateForm.remark,
      })
      toastSuccess("Vacated")
      setVacateModal(false)
      setAllotEmp(e => (e ? { ...e, current: null, current_bed_id: null, current_shared_id: null } : e))
      await loadRooms(selected); loadCamps()
    } catch (e) { toastError(e?.message || "Vacate failed") }
  }
  const saveRoom = async () => {
    if (!roomForm.room_no.trim()) return toastError("Room No is required")
    try {
      if (roomForm.id) await updateRoom(roomForm.id, roomForm)
      else await createRoom({ ...roomForm, camp_id: selected })
      toastSuccess("Room saved"); setRoomModal(false); loadRooms(selected); loadCamps()
    } catch (e) { toastError(e?.message || "Save failed") }
  }

  // ---- release (no delete) ----
  const openRelease = rm => { setReleaseForm({ id: rm.id, room_no: rm.room_no, release_date: "", remark: "" }); setReleaseModal(true) }
  const doRelease = async () => {
    try {
      await releaseRoom(releaseForm.id, { release_date: releaseForm.release_date, remark: releaseForm.remark })
      toastSuccess("Room released"); setReleaseModal(false); loadRooms(selected); loadCamps()
    } catch (e) { toastError(e?.message || "Release failed") }
  }

  const selectedCamp = camps.find(c => c.id === selected)
  // rooms that can take the employee: a free bed, or (over-capacity) sharing headroom
  const vacantRooms = rooms.filter(r => assignableBeds(r).length > 0 || (r.sharing_left || 0) > 0)
  const shownRooms = rooms.filter(rm => {
    const q = roomQuery.trim().toLowerCase()
    if (!q) return true
    return [rm.room_no, rm.category].some(v => String(v || "").toLowerCase().includes(q))
  })
  const set = (setter, k, v) => setter(f => ({ ...f, [k]: v }))

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <Breadcrumbs title="Camp Management" breadcrumbObject={[
            { title: "Camp Management", link: "/camp-manager" },
            { title: "Camp Manager", link: "#" },
          ]} />
          <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
            <div style={{ position: "relative" }}>
              <Input bsSize="sm" placeholder="Find person by name / code…" value={occQuery}
                onChange={e => setOccQuery(e.target.value)} style={{ width: 240 }} />
              {occQuery.trim().length >= 2 && (
                <div style={{ position: "absolute", top: "100%", right: 0, zIndex: 30, marginTop: 2, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, width: 380, maxHeight: 340, overflowY: "auto", boxShadow: "0 6px 18px rgba(0,0,0,.12)" }}>
                  {occResults.map((r, i) => (
                    <div key={i} role="button" className="px-3 py-2 border-bottom text-start"
                      onClick={() => { selectCamp({ id: r.camp_id }); setOccQuery(""); setOccResults([]) }}>
                      <div className="fw-500">{r.employee} {r.code ? <small className="text-muted">· {r.code}</small> : null}</div>
                      <small className="text-muted">{r.camp} · {r.room_no} · Bed {r.bed_no}{r.company ? " · " + r.company : ""}</small>
                    </div>
                  ))}
                  {!occResults.length && <div className="px-3 py-2 text-muted small">No housed person matches.</div>}
                </div>
              )}
            </div>
            <Input type="select" bsSize="sm" value={range} onChange={e => setRange(e.target.value)} style={{ width: 130 }}>
              {RANGES.map(r => <option key={r} value={r}>{r}</option>)}
            </Input>
            {range === "Custom" && (
              <>
                <Input type="date" bsSize="sm" value={from} onChange={e => setFrom(e.target.value)} style={{ width: 150 }} />
                <Input type="date" bsSize="sm" value={to} onChange={e => setTo(e.target.value)} style={{ width: 150 }} />
              </>
            )}
            <Button color="light" size="sm" onClick={() => navigate("/camp-dashboard")}>Dashboard</Button>
          </div>
        </div>

        <Row>
          {/* Camps — full width until a camp is clicked, then it shares space with the rooms column */}
          <Col md={selected ? 4 : 12}>
            <Card className="me-2">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0 fw-semibold">Camps</h6>
                  <Button color="primary" size="sm" onClick={openNewCamp}>+ Camp</Button>
                </div>
                <Table responsive borderless className="align-middle tnt-table mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      {/* full stats while the list is the main view; collapsed to name-only when a camp's rooms are open */}
                      {!selected && <th className="text-center">Rooms</th>}
                      {!selected && <th className="text-center">Capacity</th>}
                      {!selected && <th className="text-center">Occupied</th>}
                      {!selected && bedLevel && <th className="text-center">Leave</th>}
                      {!selected && <th className="text-center">Vacant</th>}
                      <th style={{ width: 70 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {camps.map(c => (
                      <tr key={c.id} className={selected === c.id ? "table-active" : ""} role="button" onClick={() => selectCamp(c)}>
                        <td>{c.name}</td>
                        {!selected && <td className="text-center">{c.rooms}</td>}
                        {!selected && <td className="text-center">{c.capacity}</td>}
                        {!selected && <td className="text-center">{c.occupied}</td>}
                        {!selected && bedLevel && <td className="text-center">{c.vacation || 0}</td>}
                        {!selected && <td className="text-center">{c.vacant}</td>}
                        <td className="text-end text-nowrap">
                          <i className="uil-pen action-icon" role="button" title="Edit" onClick={e => { e.stopPropagation(); openEditCamp(c) }} />
                          <i className={`uil-angle-${selected === c.id ? "down" : "right"} action-icon`} />
                        </td>
                      </tr>
                    ))}
                    {!camps.length && <tr><td colSpan={selected ? 2 : (bedLevel ? 7 : 6)} className="text-center text-muted">No camps</td></tr>}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>

          {/* Rooms — appears once a camp is selected */}
          {selected && (
          <Col md={8}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0 fw-semibold">{selectedCamp ? selectedCamp.name : "Rooms"}</h6>
                  <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    {bedLevel && <Button color="primary" size="sm" onClick={openAllot} disabled={!selected}>Room Allotment</Button>}
                    <Button color="light" size="sm" title="Close — back to camp list" onClick={() => { setSelected(null); setRooms([]) }}>
                      <i className="uil-times" />
                    </Button>
                  </div>
                </div>
                {selectedCamp && (
                  <div className="d-flex flex-wrap mb-3" style={{ gap: 18, fontSize: 13 }}>
                    <span className="text-muted">Rooms <strong className="text-dark">{selectedCamp.rooms}</strong></span>
                    <span className="text-muted">Capacity <strong className="text-dark">{selectedCamp.capacity}</strong></span>
                    <span className="text-muted">Occupied <strong className="text-dark">{selectedCamp.occupied}</strong></span>
                    {bedLevel && <span className="text-muted">Leave <strong className="text-dark">{selectedCamp.vacation || 0}</strong></span>}
                    <span className="text-muted">Vacant <strong className="text-dark">{selectedCamp.vacant}</strong></span>
                    {selectedCamp.location && <span className="text-muted">Location <strong className="text-dark">{selectedCamp.location}</strong></span>}
                  </div>
                )}
                <div className="mb-2">
                  <Input bsSize="sm" placeholder="Search rooms — room no / category…" value={roomQuery}
                    onChange={e => setRoomQuery(e.target.value)} style={{ maxWidth: 320 }} />
                </div>
                <Table responsive borderless className="align-middle tnt-table mb-0">
                  <thead><tr><th>Room No</th><th className="text-center">Capacity</th><th className="text-center" title="Max incl. sharing">Max</th><th className="text-center">Occupied</th>{bedLevel && <th className="text-center" title="On vacation">Leave</th>}<th className="text-center">Vacant</th><th>Category</th><th className="text-end">Price</th><th></th></tr></thead>
                  <tbody>
                    {shownRooms.map(rm => {
                      const sharedCount = (rm.shared_occupants || []).length
                      return (
                      <tr key={rm.id}>
                        <td>{rm.room_no}</td>
                        <td className="text-center">{rm.capacity}</td>
                        <td className="text-center">{rm.max_occupancy && rm.max_occupancy > rm.capacity ? rm.max_occupancy : "—"}</td>
                        <td className="text-center">{rm.occupied}{sharedCount > 0 && <span className="text-warning ms-1" title={`${sharedCount} shared`}>(+{sharedCount})</span>}</td>
                        {bedLevel && <td className="text-center">{rm.vacation || 0}</td>}
                        <td className="text-center">{rm.vacant ?? Math.max(0, (rm.ceiling ?? rm.capacity) - rm.occupied - (rm.vacation || 0))}</td>
                        <td className="text-capitalize">{(rm.category || "").toLowerCase()}</td>
                        <td className="text-end">
                          {rm.price != null && rm.price !== "" ? Number(rm.price).toLocaleString(undefined, { minimumFractionDigits: 2 }) : "—"}
                          <i className="uil-history action-icon ms-2" role="button" title="Price history" onClick={() => openPriceHistory(rm)} />
                        </td>
                        <td className="text-nowrap text-end">
                          {bedLevel && <i className="uil-bed action-icon" role="button" title="Beds" onClick={() => openBeds(rm)} />}
                          <i className="uil-pen action-icon" role="button" title="Edit" onClick={() => openEditRoom(rm)} />
                          <i className="uil-sign-in-alt action-icon" role="button" title="Occupants — move / vacate, then release" onClick={() => openOccupants(rm)} />
                        </td>
                      </tr>
                      )
                    })}
                    {!shownRooms.length && <tr><td colSpan={bedLevel ? 9 : 8} className="text-center text-muted">{rooms.length ? "No rooms match your search" : (selected ? "No rooms" : "Select a camp")}</td></tr>}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
          )}
        </Row>
      </Container>

      {/* Camp modal */}
      <Modal isOpen={campModal} toggle={() => setCampModal(false)} size="xl">
        <ModalHeader toggle={() => setCampModal(false)}>{campForm.id ? "Edit" : "New"} Camp</ModalHeader>
        <ModalBody>
          <Row>
            <Col md={5}>
              <Label>Name *</Label>
              <Input value={campForm.name} onChange={e => set(setCampForm, "name", e.target.value)} className="mb-3" />
            </Col>
            <Col md={4}>
              <Label>Location</Label>
              <Input value={campForm.location} onChange={e => set(setCampForm, "location", e.target.value)} className="mb-3" />
            </Col>
            <Col md={3}>
              <Label>Remark</Label>
              <Input value={campForm.remark} onChange={e => set(setCampForm, "remark", e.target.value)} className="mb-3" />
            </Col>
          </Row>

          {(
            <>
              <div className="d-flex justify-content-between align-items-center mb-2 mt-1">
                <Label className="mb-0 fw-500">Rooms <small className="text-muted">— {campForm.id ? "edit existing rooms or add new ones" : "add capacity + hire date per room"}</small></Label>
                <Button color="light" size="sm" onClick={addCampRoom}>+ Room</Button>
              </div>
              <style>{`
                .camp-rooms-grid table td, .camp-rooms-grid table th { padding: 9px 10px !important; vertical-align: middle; }
                .camp-rooms-grid table tbody tr + tr td { border-top: 1px solid #f0f1f4; }
              `}</style>
              <div className="camp-rooms-grid">
              <Table responsive borderless className="align-middle tnt-table mb-0" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ width: 220 }} title="Include floor, e.g. GF - 04">Room No</th>
                    <th style={{ width: 80 }}>Capacity</th>
                    <th style={{ width: 80 }} title="Max people incl. sharing (blank = no sharing)">Max</th>
                    <th style={{ width: 140 }}>Hire Date</th>
                    <th style={{ width: 120 }}>Category</th>
                    <th style={{ width: 130 }}>Price</th>
                    <th>Occupied</th>
                    <th style={{ width: 90 }} className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(campForm.rooms || []).map((r, i) => (
                    <tr key={i}>
                      <td>
                        <Input bsSize="sm" value={r.room_no} placeholder="e.g. GF - 04" onChange={e => setCampRoom(i, "room_no", e.target.value)} />
                        {!r.id && <small className="text-success">new</small>}
                      </td>
                      <td><Input bsSize="sm" type="number" min={0} value={r.capacity} onChange={e => setCampRoom(i, "capacity", e.target.value)} /></td>
                      <td><Input bsSize="sm" type="number" min={0} placeholder="—" title="Max people incl. sharing" value={r.max_occupancy ?? ""} onChange={e => setCampRoom(i, "max_occupancy", e.target.value)} /></td>
                      <td><Input bsSize="sm" type="date" value={r.hired_date} onChange={e => setCampRoom(i, "hired_date", e.target.value)} /></td>
                      <td>
                        <Input bsSize="sm" type="select" value={r.category} onChange={e => setCampRoom(i, "category", e.target.value)}>
                          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </Input>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <Input bsSize="sm" type="number" min={0} step="0.01" placeholder="0.00" value={r.price ?? ""} onChange={e => setCampRoom(i, "price", e.target.value)} />
                          {r.id && <i className="uil-history action-icon text-muted" role="button" title="Price history" onClick={() => openPriceHistory(r)} />}
                        </div>
                      </td>
                      <td>
                        {r.id ? (() => {
                          const cap = Number(r.ceiling || r.capacity || 0)
                          const occ = Number(r.occupied || 0)
                          const pct = cap ? Math.min(100, Math.round((occ / cap) * 100)) : 0
                          const full = cap && occ >= cap
                          return (
                            <div style={{ minWidth: 120 }}>
                              <div className="d-flex justify-content-between" style={{ fontSize: 12 }}>
                                <span>{occ} / {cap}{r.shared > 0 ? <span className="text-warning"> (+{r.shared})</span> : null}</span>
                                <span className="text-muted">{full ? "full" : `${Math.max(0, cap - occ)} free`}</span>
                              </div>
                              <div style={{ height: 4, borderRadius: 3, background: "#eef0f3" }}>
                                <div style={{ height: 4, borderRadius: 3, width: `${pct}%`, background: full ? "#f1556c" : "var(--accent-clr, #3b82f6)" }} />
                              </div>
                            </div>
                          )
                        })() : <span className="text-success small">new</span>}
                      </td>
                      <td className="text-end text-nowrap">
                        {!r.id
                          ? <i className="uil-trash-alt action-icon text-danger" role="button" title="Remove" onClick={() => removeCampRoom(i)} />
                          : <>
                              <i className="uil-eye action-icon text-muted me-2" role="button" title="View occupants" onClick={() => openViewOccupants(r)} />
                              <i className="uil-sign-in-alt action-icon text-muted" role="button" title="Manage occupants — move / vacate, then release" onClick={() => openOccupants(r)} />
                            </>}
                      </td>
                    </tr>
                  ))}
                  {!(campForm.rooms || []).length && <tr><td colSpan="8" className="text-center text-muted">{campForm.id ? "Loading rooms…" : "No rooms — click “+ Room”."}</td></tr>}
                </tbody>
              </Table>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setCampModal(false)}>Cancel</Button>
          <Button color="primary" onClick={saveCamp}>Save</Button>
        </ModalFooter>
      </Modal>

      {/* Room modal */}
      <Modal isOpen={roomModal} toggle={() => setRoomModal(false)}>
        <ModalHeader toggle={() => setRoomModal(false)}>{roomForm.id ? "Edit" : "New"} Room</ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6} className="mb-3"><Label>Room No *</Label><Input value={roomForm.room_no} placeholder="include floor, e.g. GF - 04" onChange={e => set(setRoomForm, "room_no", e.target.value)} /></Col>
            <Col md={6} className="mb-3"><Label>Category</Label>
              <Input type="select" value={roomForm.category} onChange={e => set(setRoomForm, "category", e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Input>
            </Col>
            <Col md={4} className="mb-3"><Label>Capacity{bedLevel ? " (beds)" : ""}</Label><Input type="number" min={0} value={roomForm.capacity} onChange={e => set(setRoomForm, "capacity", e.target.value)} /></Col>
            {!bedLevel && <Col md={4} className="mb-3"><Label>Occupied</Label><Input type="number" min={0} value={roomForm.occupied} onChange={e => set(setRoomForm, "occupied", e.target.value)} /></Col>}
            <Col md={4} className="mb-3"><Label>Max (sharing)</Label><Input type="number" min={0} placeholder="—" value={roomForm.max_occupancy ?? ""} onChange={e => set(setRoomForm, "max_occupancy", e.target.value)} /></Col>
            <Col md={4} className="mb-3"><Label>Hired Date</Label><Input type="date" value={roomForm.hired_date} onChange={e => set(setRoomForm, "hired_date", e.target.value)} /></Col>
            <Col md={4} className="mb-3"><Label>Price</Label><Input type="number" min={0} step="0.01" placeholder="0.00" value={roomForm.price ?? ""} onChange={e => set(setRoomForm, "price", e.target.value)} /></Col>
          </Row>
          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Max (sharing) is the room's people limit incl. bed-sharing — leave blank for none (limit = capacity).{roomForm.id ? " Changing the price keeps a history entry." : ""}</p>
          {bedLevel && <p className="text-muted mb-0" style={{ fontSize: 12 }}>Capacity creates that many beds. Manage each bed's occupancy from the <strong>Beds</strong> action on the room.</p>}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setRoomModal(false)}>Cancel</Button>
          <Button color="primary" onClick={saveRoom}>Save</Button>
        </ModalFooter>
      </Modal>

      {/* Release modal */}
      <Modal isOpen={releaseModal} toggle={() => setReleaseModal(false)}>
        <ModalHeader toggle={() => setReleaseModal(false)}>Release Room {releaseForm.room_no}</ModalHeader>
        <ModalBody>
          <Label>Release Date</Label>
          <Input type="date" value={releaseForm.release_date} onChange={e => set(setReleaseForm, "release_date", e.target.value)} className="mb-3" />
          <Label>Remarks (reason / purpose)</Label>
          <Input type="textarea" rows={3} value={releaseForm.remark} onChange={e => set(setReleaseForm, "remark", e.target.value)} />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setReleaseModal(false)}>Cancel</Button>
          <Button color="danger" onClick={doRelease}>Release</Button>
        </ModalFooter>
      </Modal>

      {/* Beds modal (bed-level only) — set each bed's occupancy status */}
      <Modal isOpen={bedsModal} toggle={() => setBedsModal(false)} size="lg">
        <ModalHeader toggle={() => setBedsModal(false)}>
          Beds — {bedsRoom?.room_no}
        </ModalHeader>
        <ModalBody>
          <Table responsive borderless className="align-middle tnt-table mb-0">
            <thead><tr><th>Bed</th><th>Occupant</th><th style={{ width: 200 }}>Status</th></tr></thead>
            <tbody>
              {bedList.map(b => (
                <tr key={b.id}>
                  <td>{b.bed_no}</td>
                  <td>
                    {b.occupant
                      ? <span>{b.occupant.name}{b.occupant.company ? <small className="text-muted d-block" title={b.occupant.company_name}>{b.occupant.company}</small> : null}</span>
                      : <span className="text-muted">—</span>}
                  </td>
                  <td>
                    <Input type="select" bsSize="sm" value={b.status} onChange={e => changeBedStatus(b, e.target.value)}>
                      {BED_STATUS.map(s => <option key={s} value={s} className="text-capitalize">{s}</option>)}
                    </Input>
                  </td>
                </tr>
              ))}
              {!bedList.length && <tr><td colSpan="3" className="text-center text-muted">No beds — set a capacity on the room to create beds.</td></tr>}
            </tbody>
          </Table>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setBedsModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Room Allotment modal — assign workforce employees to beds (replaces the old bed picker on the employee form) */}
      <Modal isOpen={allotModal} toggle={() => setAllotModal(false)} size="lg">
        <ModalHeader toggle={() => setAllotModal(false)}>Room Allotment — {selectedCamp?.name}</ModalHeader>
        <ModalBody>
          {/* Step 1 — find the employee */}
          <Label className="fw-500">Find employee to allot</Label>
          <div style={{ position: "relative" }}>
            <Input placeholder="Search by name / code…"
              value={allotEmp ? `${allotEmp.name}${allotEmp.code ? " · " + allotEmp.code : ""}${allotEmp.company ? " · " + allotEmp.company : ""}` : allotQuery}
              onChange={e => { setAllotEmp(null); setAllotQuery(e.target.value) }} />
            {allotEmp && (
              <i className="uil-times-circle" role="button" title="Clear" onClick={() => { setAllotEmp(null); setAllotQuery("") }}
                style={{ position: "absolute", right: 10, top: 9, color: "#9ca3af", fontSize: 18 }} />
            )}
          </div>

          {/* search results — inline (in the flow) so they're never clipped by the modal */}
          {!allotEmp && allotQuery.trim().length >= 2 && (
            <div className="border rounded mt-2" style={{ maxHeight: 260, overflowY: "auto" }}>
              {allotResults.map(r => (
                <div key={r.id} role="button" className="px-3 py-2 border-bottom text-start" onClick={() => { setAllotEmp(r); setAllotResults([]) }}>
                  <div className="fw-500">{r.name} {r.code ? <small className="text-muted">· {r.code}</small> : null} {r.company ? <span className="badge bg-light text-dark ms-1">{r.company}</span> : null}</div>
                  <small className="text-muted">{r.current ? "housed: " + r.current : "not housed"}</small>
                </div>
              ))}
              {!allotResults.length && <div className="px-3 py-2 text-muted small">No employee matches.</div>}
            </div>
          )}

          {/* Step 2 — vacant rooms + bed for the chosen employee */}
          {!allotEmp ? (
            allotQuery.trim().length >= 2 ? null : <p className="text-muted mt-3 mb-0">Search and select an employee to see vacant rooms.</p>
          ) : (
            <>
              {allotEmp.current && (
                <div className="alert alert-warning d-flex justify-content-between align-items-center mt-3 mb-2 py-2 px-3">
                  <span>Currently housed in <strong>{allotEmp.current}</strong> — allotting will move them.</span>
                  <Button color="light" size="sm" onClick={openVacate}>Vacate</Button>
                </div>
              )}
              <Row className="mt-3 mb-1">
                <Col sm={5}>
                  <Label className="fw-500 mb-1">Allotment date</Label>
                  <Input bsSize="sm" type="date" value={allotDate} onChange={e => setAllotDate(e.target.value)} />
                </Col>
              </Row>
              <Label className="fw-500 mt-2 mb-2 d-block">Available rooms <small className="text-muted">— pick a bed for {allotEmp.name} (or share when a room is full)</small></Label>
              <Table responsive borderless className="align-middle tnt-table mb-0">
                <thead><tr><th>Room No</th><th className="text-center" style={{ width: 90 }}>Free</th><th style={{ width: 160 }}>Bed</th><th className="text-end" style={{ width: 140 }}>Action</th></tr></thead>
                <tbody>
                  {vacantRooms.map(r => {
                    const beds = assignableBeds(r)
                    const hasBed = beds.length > 0
                    const sel = allotBedSel[r.id] ?? String((hasBed ? beds[0]?.id : (r.beds || [])[0]?.id) || "")
                    return (
                      <tr key={r.id}>
                        <td>{r.room_no}{!hasBed && (r.sharing_left || 0) > 0 && <span className="badge bg-warning-subtle text-warning ms-2">sharing</span>}</td>
                        <td className="text-center">{hasBed ? beds.length : <span className="text-warning">+{r.sharing_left} shared</span>}</td>
                        <td>
                          {hasBed ? (
                            <Input bsSize="sm" type="select" value={sel} onChange={e => setAllotBedSel(s => ({ ...s, [r.id]: e.target.value }))}>
                              {beds.map(b => <option key={b.id} value={b.id}>Bed {b.bed_no}</option>)}
                            </Input>
                          ) : (
                            <Input bsSize="sm" type="select" value={sel} onChange={e => setAllotBedSel(s => ({ ...s, [r.id]: e.target.value }))}>
                              <option value="">(share — no fixed bed)</option>
                              {(r.beds || []).map(b => <option key={b.id} value={b.id}>Share Bed {b.bed_no}</option>)}
                            </Input>
                          )}
                        </td>
                        <td className="text-end">
                          {hasBed
                            ? <Button color="primary" size="sm" onClick={() => allotToBed(Number(sel))}>Allot</Button>
                            : <Button color="warning" size="sm" onClick={() => allotSharedToRoom(r.id, sel ? Number(sel) : null)}>Allot (shared)</Button>}
                        </td>
                      </tr>
                    )
                  })}
                  {!vacantRooms.length && <tr><td colSpan="4" className="text-center text-muted">No free beds or sharing space in this camp.</td></tr>}
                </tbody>
              </Table>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setAllotModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Vacate modal — capture date + reason + details when freeing a bed */}
      <Modal isOpen={vacateModal} toggle={() => setVacateModal(false)}>
        <ModalHeader toggle={() => setVacateModal(false)}>Vacate bed</ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6} className="mb-3">
              <Label>Vacate date *</Label>
              <Input type="date" value={vacateForm.date} onChange={e => set(setVacateForm, "date", e.target.value)} />
            </Col>
            <Col md={6} className="mb-3">
              <Label>Reason *</Label>
              <Input value={vacateForm.reason} placeholder="e.g. exit, transfer, on leave" onChange={e => set(setVacateForm, "reason", e.target.value)} />
            </Col>
            <Col md={12} className="mb-1">
              <Label>Details</Label>
              <Input type="textarea" rows={3} value={vacateForm.remark} placeholder="Any additional notes" onChange={e => set(setVacateForm, "remark", e.target.value)} />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setVacateModal(false)}>Cancel</Button>
          <Button color="primary" onClick={doVacate}>Vacate</Button>
        </ModalFooter>
      </Modal>

      {/* Price history modal */}
      <Modal isOpen={priceHistModal} toggle={() => setPriceHistModal(false)}>
        <ModalHeader toggle={() => setPriceHistModal(false)}>Price history — {priceHistRoom?.room_no}</ModalHeader>
        <ModalBody>
          <Table responsive borderless className="align-middle tnt-table mb-0">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Effective date</th>
                <th className="text-end" style={{ width: 120 }}>Price</th>
                <th style={{ width: 150, paddingLeft: 24 }}>Added by</th>
                <th>Remark</th>
              </tr>
            </thead>
            <tbody>
              {priceHist.map(h => (
                <tr key={h.id}>
                  <td>{h.effective_date}</td>
                  <td className="text-end">{Number(h.price).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td style={{ paddingLeft: 24 }}>{h.added_by || "—"}</td>
                  <td className="text-muted">{h.remark || "—"}</td>
                </tr>
              ))}
              {!priceHist.length && <tr><td colSpan="4" className="text-center text-muted">No price history yet.</td></tr>}
            </tbody>
          </Table>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setPriceHistModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Occupants manager — move / vacate each occupant, then release the (empty) room */}
      <Modal isOpen={occModal} toggle={() => setOccModal(false)} size="lg">
        <ModalHeader toggle={() => setOccModal(false)}>Occupants — {occRoom?.room_no}</ModalHeader>
        <ModalBody>
          <Row className="mb-3">
            <Col sm={4}>
              <Label className="fw-500 mb-1">Date</Label>
              <Input bsSize="sm" type="date" value={occDate} onChange={e => setOccDate(e.target.value)} />
            </Col>
            <Col sm={8}>
              <Label className="fw-500 mb-1">Common reason <small className="text-muted">(applied to everyone you vacate)</small></Label>
              <Input bsSize="sm" value={occReason} placeholder="e.g. camp closed, room released" onChange={e => setOccReason(e.target.value)} />
            </Col>
          </Row>

          {(() => {
            const list = roomOccupants(occRoom)
            // group move targets by camp (across all camps) for the picker
            const byCamp = (occTargets || []).reduce((m, t) => {
              (m[t.camp_name] = m[t.camp_name] || []).push(t); return m
            }, {})
            return (
              <>
                <Label className="fw-500 mb-2 d-block">
                  {list.length ? `${list.length} occupant(s) — move each elsewhere or vacate` : "No occupants — this room is empty."}
                </Label>
                {!!list.length && (
                  <Table responsive borderless className="align-middle tnt-table mb-0">
                    <thead><tr><th>Occupant</th><th style={{ width: 90 }}>Bed</th><th style={{ width: 200 }}>Move to</th><th className="text-end" style={{ width: 170 }}>Action</th></tr></thead>
                    <tbody>
                      {list.map(emp => (
                        <tr key={`${emp.id}-${emp.shared ? "s" : "b"}`}>
                          <td>
                            <div className="fw-500">{emp.name}</div>
                            <small className="text-muted">{emp.amj_code}{emp.company ? " · " + emp.company : ""}</small>
                          </td>
                          <td>{emp.shared ? <span className="badge bg-warning-subtle text-warning">shared</span> : `Bed ${emp.bed_no}`}</td>
                          <td>
                            <Input bsSize="sm" type="select" value={occDestSel[emp.id] || ""} onChange={e => setOccDestSel(s => ({ ...s, [emp.id]: e.target.value }))}>
                              <option value="">Select room…</option>
                              {Object.entries(byCamp).map(([camp, rms]) => (
                                <optgroup key={camp} label={camp}>
                                  {rms.map(d => (
                                    <option key={d.room_id} value={d.room_id}>
                                      {d.room_no} ({d.free_beds > 0 ? `${d.free_beds} free` : `+${d.sharing_left} shared`})
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                            </Input>
                          </td>
                          <td className="text-end text-nowrap">
                            <Button color="primary" size="sm" className="me-1" onClick={() => moveOccupant(emp)}>Move</Button>
                            <Button color="light" size="sm" onClick={() => vacateOccupant(emp)}>Vacate</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </>
            )
          })()}
        </ModalBody>
        <ModalFooter className="justify-content-between">
          <small className="text-muted">Release is enabled once the room is empty.</small>
          <div>
            <Button color="light" className="me-2" onClick={() => setOccModal(false)}>Close</Button>
            <Button color="danger" disabled={roomOccupants(occRoom).length > 0} onClick={releaseFromOccupants}>Release room</Button>
          </div>
        </ModalFooter>
      </Modal>

      {/* View occupants — read-only: who's staying, their company, how long */}
      <Modal isOpen={occViewModal} toggle={() => setOccViewModal(false)} size="lg">
        <ModalHeader toggle={() => setOccViewModal(false)}>Occupants — {occViewRoom?.room_no}</ModalHeader>
        <ModalBody>
          <style>{`.occ-view-table thead th, .occ-view-table tbody td { padding: 12px 14px !important; text-align: left; }`}</style>
          <Table responsive borderless className="align-middle tnt-table mb-0 occ-view-table">
            <thead>
              <tr>
                <th>Occupant</th>
                <th>Company</th>
                <th style={{ width: 90 }}>Bed</th>
                <th style={{ width: 130 }}>Staying since</th>
                <th style={{ width: 120 }}>Duration</th>
              </tr>
            </thead>
            <tbody>
              {occViewList.map(o => (
                <tr key={o.employee_id}>
                  <td>
                    <div className="fw-500">{o.name}</div>
                    <small className="text-muted">{o.code}</small>
                  </td>
                  <td title={o.company_name || ""}>{o.company || "—"}</td>
                  <td>{o.shared ? <span className="badge bg-warning-subtle text-warning">shared</span> : o.bed}</td>
                  <td>{o.since || "—"}</td>
                  <td>{stayDuration(o.days)}</td>
                </tr>
              ))}
              {!occViewList.length && <tr><td colSpan="5" className="text-center text-muted">No occupants in this room.</td></tr>}
            </tbody>
          </Table>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setOccViewModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default CampManager
